/** Obstacle placement: which sprite a blocking prop gets, how a formation
 * is spread over its cells, its exposure, dressing and shadow, plus the
 * fixture obstacle lists the screenshot scenes use. Split out of `arena.ts`
 * on 2026-09-17; `Arena.placeObstacles` consumes these and the layer model
 * is in that file's header.
 */

import type { Obstacle } from "@game/core";
import { BF_HEIGHT, BF_WIDTH, hexCol, hexIndex, hexNeighbours, hexRow } from "@game/core";
import { mixColor, noise } from "../theme.js";
import { coverKeys, coverLevel, decalKeys } from "../keys.js";
import { GROUND_GRADE, groundLevel } from "./backdrop.js";
import { WALL_DAMAGED, WALL_INTACT } from "./masonry.js";

/**
 * Props a scene may use as **scenery**: everything in the arena's palette that
 * is not blocking cover on that arena.
 *
 * This used to be a deny-list here (`LITTER`) and a `coverKey` that filtered the
 * shared palette through it - the same knowledge written twice, in the file that
 * draws props rather than in the file that knows what each prop *is*.
 * `keys.ts` now splits every arena's palette into `cover` and `litter`, so the
 * two questions have one answer each: `obstacleKey` picks over `cover`, and
 * scenery picks over what is left.
 *
 * The direction matters more than the tidiness. This build's whole claim is that
 * what is painted is what the rules block on, and a decorative object *identical
 * to a blocker* breaks that at a glance - the critic caught the same well drawn
 * as a blocking obstacle at one place on the board and as pure margin dressing
 * at another. Nothing drawn as scenery may be a sprite that blocks a cell.
 */
export function sceneryKeys(terrain: string): readonly string[] {
  const cover = new Set(coverKeys(terrain));
  return decalKeys(terrain).filter((k) => !cover.has(k));
}

/**
 * How far apart two blocking props have to be before drawing the same sprite
 * for both stops reading as a copy-paste.
 *
 * `obstacleKey` picks by a hash of the hex index, which is stable and
 * uncorrelated - and uncorrelated is exactly the problem. With five sprites in
 * the palette a given pair of cells collides one time in five, and the board
 * carries a dozen obstacles, so a collision somewhere on the field is the
 * normal case rather than bad luck: the shipped shots show two identical slag
 * heaps two columns apart on wasteland and the same thorn ball three times on
 * highlands. Two columns is 120 arena pixels, which at 1.5x is close enough
 * that both are inside one glance.
 *
 * The rotation is over the arena's own `cover` list in order, so it is a
 * function of the obstacle list and nothing else - the same battle draws the
 * same board on every screenshot, which is what the seeded scatter is for. A
 * key the kernel authored is never rotated: `Obstacle.art` is the kernel's
 * decision and this is only breaking ties in ours.
 */
// The whole board: a sprite is not picked again until every other cover
// sprite of the arena has been used. At 2x1 a critic counted the same boulder
// twice and the same log pile twice among six obstacles on dirt.
const NEAR_COLS = BF_WIDTH;
const NEAR_ROWS = BF_HEIGHT;

export function unrepeated(
  key: string, o: Obstacle, cover: readonly string[], drawn: ReadonlyMap<number, string>,
): string {
  if (o.art.startsWith("obj.") || cover.length < 2) return key;
  const from = cover.indexOf(key.slice("obj.".length));
  if (from < 0) return key;
  const col = hexCol(o.hex), row = hexRow(o.hex);
  const near = new Set<string>();
  for (const [hex, k] of drawn) {
    if (Math.abs(hexCol(hex) - col) <= NEAR_COLS && Math.abs(hexRow(hex) - row) <= NEAR_ROWS) {
      near.add(k);
    }
  }
  for (let step = 0; step < cover.length; step++) {
    const cand = `obj.${cover[(from + step) % cover.length]!}`;
    if (!near.has(cand)) return cand;
  }
  return key;
}

/**
 * The exposure a blocking obstacle is drawn at on this arena, as a Pixi tint.
 *
 * Obstacles are z-sorted into the shared actor container, so they are drawn
 * *above* `paintGroundGrade` and have to carry their own exposure. That used to
 * be a flat `GROUND_GRADE` tint on all nineteen arenas, which models the
 * grade and nothing else, and the grade is the one part of the field's exposure
 * that is already the same everywhere. What it misses is the plate: a sprite
 * authored at a generic brightness standing on a board at luma 29 is not lit by
 * that board's light.
 *
 * Measured as the sprite's own opaque-pixel luma times its tint over the arena's
 * open ground - the prop-to-ground ratio, which is the quantity a viewer reads -
 * the twenty-one arenas run from 0.37 (magic plains) to 2.04 (evil fog) on a
 * flat tint. Four of them measured on the shipped 1920x1080 frames, on the
 * sprites' **own pixels** - each prop's alpha mask projected into the frame from
 * the atlas at the placement this method computes, eroded a pixel to drop the
 * keyed rim, against the arena's unshaded open board:
 *
 *            before   after
 *   lava      1.28     0.76
 *   cavern    1.28     0.76
 *   dirt      0.81     0.81
 *   snow      0.49     0.58
 *
 * The cell-interior-against-neighbour-ring measure this was first reported on is
 * not usable for it and it is worth saying why, because it reads like a smaller
 * version of the same number and moves the opposite way. A prop's body lands in
 * the cell *above* the one it stands in, so the inner mask catches its foot and
 * the ring catches its body; and the ring is partly reach-shaded ground, so
 * changing the reach shade moves the ratio without touching a prop. Run over
 * this same pair of frames it reports lava going 1.44 -> 1.54 and snow 0.80 ->
 * 0.67, which is the reach pass showing through, not the props.
 *
 * **Dirt is the anchor and does not move.** It is the arena the ground grade,
 * the lattice and the creature exposure were all calibrated on, so its props are
 * the ratio the other twenty are brought to:
 *
 *   exposure(t) = GROUND_GRADE * (ground(t)/cover(t)) / (ground(dirt)/cover(dirt))
 *
 * with `ground` from `groundLevel` and `cover` from `coverLevel`, clamped into
 * `[OBSTACLE_EXPOSURE_FLOOR, 1]`. That takes the spread from 0.37..2.04 to
 * 0.44..1.20, with seven arenas landing exactly on dirt's 0.75.
 *
 * Both clamps bite and both are honest limits rather than tuning. A tint can
 * only darken, so the ten arenas whose plate outshines their palette - snow,
 * sand, holy ground, magic plains, magic clouds, lucid pools, cursed ground,
 * fields of glory, swamp, wasteland - cannot be brought all the way up; dropping
 * the grade off them is the whole of the lift they can get. And the floor stops
 * the four darkest plates - lava, evil fog, cavern, water - from turning their
 * props into silhouettes: evil fog wants 0.31 and is held at 0.50, which leaves
 * it the widest miss on the board at 1.20.
 *
 * For scale, the classic's own props measured on a reference screenshot (the publisher's the classic
 * gallery shot, panel row 2 column 2) sit at 0.78 for the stump and 1.14 for the
 * rock cluster against their unshaded ground - so the band this lands in is the
 * reference's band, a little under it, which is where the rest of our art
 * already sits (see `GROUND_GRADE` on the roster running 68.5 against 81.4).
 */
export const OBSTACLE_EXPOSURE_FLOOR = 0.5;

/**
 * The kernel's formations, recovered from the obstacle list: the connected
 * groups of blocked cells, each in hex order with its head cell first. The
 * list itself carries no grouping - `fieldObstacles` sorts every cell into one
 * order - and adjacency is the only thing that says which cells were laid as
 * one thing. Formations are kept a clear cell apart by the generator, so two
 * of them never touch and cannot be merged by this.
 */
export function obstacleFormations(hexes: readonly number[]): readonly (readonly number[])[] {
  const set = new Set(hexes);
  const seen = new Set<number>();
  const out: number[][] = [];
  const nb: number[] = [0, 0, 0, 0, 0, 0];
  for (const start of [...hexes].sort((a, b) => a - b)) {
    if (seen.has(start)) continue;
    const group: number[] = [];
    const queue = [start];
    seen.add(start);
    while (queue.length > 0) {
      const h = queue.shift()!;
      group.push(h);
      const k = hexNeighbours(h, nb);
      for (let m = 0; m < k; m++) {
        const n = nb[m]!;
        if (!set.has(n) || seen.has(n)) continue;
        seen.add(n);
        queue.push(n);
      }
    }
    group.sort((a, b) => a - b);
    out.push(group);
  }
  return out;
}

export function obstacleExposure(terrain: string): number {
  const ref = groundLevel("dirt") / coverLevel("dirt");
  const here = groundLevel(terrain) / coverLevel(terrain);
  const e = GROUND_GRADE * (here / ref);
  return Math.max(OBSTACLE_EXPOSURE_FLOOR, Math.min(1, e));
}

export function obstacleTint(terrain: string): number {
  return mixColor(0xffffff, 0x000000, 1 - obstacleExposure(terrain));
}

/**
 * Where a blocking prop actually stands inside its cell, and what stands with
 * it.
 *
 * The defect is one measurement in two halves. Over the 99 palette entries of
 * the twenty-one arenas (85 distinct sprites) the drawn width of a blocking
 * prop was a median **0.747 HEX_W** with 74% of them narrower than a single
 * cell, and every one was planted exactly on `hexGroundLine(hex)` - so the
 * variance of the centroid's offset from the cell centre was not small, it was
 * **zero**. Both blind judges put "one object, dead-centre, clearly bounded"
 * among their top two battle-screen tells.
 *
 * The classic does not do that. Measured by hand off the publisher's own the classic gallery
 * shot a reference screenshot (710x533, a downscale of 800x600, so one column
 * pitch is 39.9 px in the file) against a ruler drawn at that pitch: the pond
 * spans 4.4 HEX_W, the ravine 6.1, the boulder pile 2.0, the white rock cluster
 * 1.7 - which visibly overhangs its cell's boundary - and the tree stump, the
 * only single-cell object on that board, 0.85 while sitting left of its own
 * cell's centre. Median 2.0 against our 0.75, and not one of the five is
 * centred or bounded by its cell.
 *
 * Ours cannot answer that with multi-cell art: the placer guarantees no two
 * obstacles are adjacent (see `fixtureFieldObstacles`), so there is never a run
 * of blocked cells for one piece to span. What is available is the other half
 * of what the reference's rock cluster does - a **mass** instead of an object.
 * The prop is pushed off its cell's centre, mirrored and sized per cell, and a
 * skirt of the arena's own **litter** is laid low around its foot.
 *
 * Litter and not cover, and that is the whole of why this stays honest. This
 * file's contract is that a cover sprite marks a cell a stack may not enter
 * (`sceneryKeys`, and the three tests in `battle-arena.spec.ts` that hold it),
 * so the skirt is drawn from the list that means the opposite everywhere else
 * on the board and can never be read as a second blocker. The mass overhangs -
 * `OBSTACLE_REACH_MAX` lets it reach 0.85 of a cell width from the centre
 * against the hexagon's own 0.5, which is about what the reference's rock
 * cluster does - and the blocking cell stays the one the main prop's body
 * stands over, because the nudge is a fifth of a cell and the skirt is ground
 * litter.
 *
 * Everything here is a pure function of the hex index, like `obstacleKey`: the
 * same battle draws the same mass in the same place, and no draw call reads a
 * clock or the battle seed.
 *
 * What it comes to, recomputed by `battle-obstacle-mass.spec.ts` off the
 * shipped atlas manifest and this function rather than quoted: the drawn mass
 * is a median **1.069 HEX_W** against the bare sprite's 0.747, the share
 * narrower than one cell falls from 73.7% to 40.9% - the floor being
 * `hexed_soil`, the one arena with no litter in its palette at all, whose
 * six props keep the bare width - and the centroid offset, which was exactly
 * zero on every prop of every arena, has sd 0.081 HEX_W over the range
 * -0.24..+0.22 with a mean of -0.001. Nothing in any mass reaches past
 * `OBSTACLE_REACH_MAX`.
 */
export interface ObstacleSkirt {
  /** Offset from the cell's ground point, in HEX_W and in row pitches. */
  readonly dx: number;
  readonly dy: number;
  /** Height cap for this piece, in row pitches. */
  readonly k: number;
  /** Index into the arena's litter list. */
  readonly pick: number;
  readonly mirror: boolean;
}

export interface ObstacleDressing {
  /** Offset of the prop's foot from the cell's ground point, in HEX_W / pitches. */
  readonly dx: number;
  readonly dy: number;
  /** Size multiplier applied inside the `OBSTACLE_SCALE` cap, never on top. */
  readonly vary: number;
  readonly mirror: boolean;
  readonly skirt: readonly ObstacleSkirt[];
}

/**
 * The furthest any part of an obstacle's mass may sit from its cell's centre,
 * in HEX_W. The cell's own hexagon is 0.5 wide either side, so this allows 0.35
 * of overhang - about what the reference's rock cluster does over its
 * neighbour - and stops short of the next cell's centre at 1.0. It is a hard
 * clamp and not a budget: a sprite already wider than the allowance gives up
 * its nudge and stands on the cell centre rather than reaching further.
 */
export const OBSTACLE_REACH_MAX = 0.85;

/** `dx` clamped so a piece of that half-width cannot break `OBSTACLE_REACH_MAX`. */
export function obstacleOffset(dx: number, halfWidth: number): number {
  const room = OBSTACLE_REACH_MAX - halfWidth;
  return room <= 0 ? 0 : Math.max(-room, Math.min(room, dx));
}

/**
 * How tall a multi-cell piece may stand, in row pitches, and how far past its
 * own aspect it may be widened to reach its cells. See `Arena.placeFormation`.
 */
export const FORMATION_MAX_PITCHES = 1.75;
export const FORMATION_WIDEN = 1.3;

/** How far off its cell's centre a prop's foot may be pushed. */
export const OBSTACLE_NUDGE_X = 0.2;
export const OBSTACLE_NUDGE_Y = 0.14;
/** How far out the skirt reaches. The cell's own half-width is 0.5 HEX_W. */
export const OBSTACLE_SKIRT_REACH = 0.56;

/** Fixed seed: the dressing is a property of the cell, not of the battle. */
const DRESS_SEED = 0x5f3a;

export function obstacleDressing(hex: number): ObstacleDressing {
  const n = (salt: number): number => noise(DRESS_SEED, hex * 37 + salt);
  const skirt: ObstacleSkirt[] = [];
  // Two or three pieces, on stratified angles so a pair lands on opposite
  // sides of the foot rather than in one heap. One is a pebble beside a rock;
  // four is a rockery.
  const count = 2 + Math.floor(n(11) * 2);
  for (let i = 0; i < count; i++) {
    const a = (i / count) * Math.PI * 2 + n(20 + i) * 1.9;
    const r = 0.6 + n(30 + i) * 0.4;
    skirt.push({
      dx: Math.cos(a) * OBSTACLE_SKIRT_REACH * r,
      // Half the reach vertically: the ground plane is squashed, and a piece
      // thrown a whole pitch up sits in the row behind rather than at the foot.
      dy: Math.sin(a) * 0.3 * r,
      k: 0.24 + n(40 + i) * 0.22,
      pick: Math.floor(n(50 + i) * 97),
      mirror: n(60 + i) > 0.5,
    });
  }
  return {
    dx: (n(1) - 0.5) * 2 * OBSTACLE_NUDGE_X,
    dy: (n(2) - 0.5) * 2 * OBSTACLE_NUDGE_Y,
    vary: 0.86 + n(3) * 0.34,
    mirror: n(4) > 0.5,
    skirt,
  };
}

/**
 * The projection a blocking prop casts, matching `stacks.ts`'s creature shadow
 * in **direction** and deliberately not in depth.
 *
 * The squash and the shear are that file's `SHADOW_SQUASH` / `SHADOW_SHEAR`,
 * repeated rather than imported for the reason they have to be equal in the
 * first place: one sun over this battlefield, so a creature and the rock beside
 * it lie the same way, and a shared import would hide a divergence behind a
 * name. `stacks.ts` owns the creature's; this is the obstacles'.
 *
 * The alpha is where they now differ, and it is measured, not sloppy.
 * `stacks.ts` raised its creature projection to 0.50 because the classic's creature
 * DEFs carry a baked shadow at black alpha 0.498 and 0.749 - an exact figure off
 * 77 exported sheets. The classic's **obstacles** carry none. On a reference screenshot at
 * 8x, the grass at the foot of the tree stump is the same grass as the rest of
 * its cell; the shading is inside the sprite and nothing is thrown onto the
 * terrain.
 *
 * A previous pass put a number on that - "2.9 luma darker than clear grass on
 * the same rows". **That number was re-measured on 2026-08-30 and could not be
 * reproduced, and this frame cannot support one.** The stump straddles the
 * boundary of the frame's own movement-range shading, which moves grass on the
 * same rows from luma 37 on one side to 66 on the other: twenty times the effect
 * being looked for. The frames on disk settle the *sign* and not the size.
 *
 * So the obstacles stay at 0.32 rather than following the creatures up to 0.50.
 * Ours already casts more than the reference does; the direction the evidence
 * points is down, and 0.32 is as far down as it goes while a prop still sits on
 * the ground rather than in front of it.
 */
export const OBSTACLE_SHADOW_SQUASH = 0.29;
export const OBSTACLE_SHADOW_SHEAR = 0.1;
export const OBSTACLE_SHADOW_SKEW = -Math.atan(OBSTACLE_SHADOW_SHEAR / OBSTACLE_SHADOW_SQUASH);
export const OBSTACLE_SHADOW_ALPHA = 0.32;

/**
 * The obstacle's contact ellipse, as fractions of the prop's drawn half-width,
 * and its strength.
 *
 * Same story as `CORE_R` / `CORE_STRENGTH` in `stacks.ts` and for the same
 * reason: the ellipse is the join under the foot, and at the radius and depth it
 * had it read as a disc instead. Isolated on the composited 1080p frame over
 * `battle`, `battle-snow` and `battle-lava`, the obstacle ellipse layer's
 * deepest pixel was 116.4 luma against the obstacle projection's 101.3, and
 * 59-71% of what it darkened lay outside the projection's own footprint.
 *
 * `OBSTACLE_CORE_STRENGTH` is `paintContactShadow`'s clamp floor, which
 * composes to 0.30 black - just under the projection's 0.32, so on an obstacle
 * too the darkest shape on the ground is silhouette-shaped.
 */
export const OBSTACLE_CORE_RX = 0.17;
export const OBSTACLE_CORE_RY = 0.065;
export const OBSTACLE_CORE_STRENGTH = 0.25;

/**
 * A siege line for the screenshot scenes. The kernel does not yet author siege
 * obstacles (`STATUS.md`: "the adventure side does not even build a siege
 * input"), and `battle-siege` still has to be reachable through `showScene`.
 * This is fixture data for the renderer, never an input to a rule.
 */
export function fixtureSiegeObstacles(fortLevel: number): readonly Obstacle[] {
  const col = 11;
  const out: Obstacle[] = [];
  const at = (row: number): number => row * BF_WIDTH + col;
  // The works run the **full height of the field**, edge to edge. Stopping a row
  // short of each edge left open grass at the top and bottom of the wall line:
  // the defenders were not enclosed by anything, and an attacker could simply
  // walk round the end of a fortification he is supposed to have to breach.
  //
  // The flanking turrets stand on rows 1 and 10, not on rows 0 and 10: a turret
  // on the back row has 64px of headroom under the initiative bar, which is not
  // enough for its battlement and its roof, and a roofless stump is
  // indistinguishable from another bay of curtain. Row 0 carries a plain bay, so
  // the wall line itself is complete from edge to edge. The third turret is the
  // keep, standing behind the gate on the defender's side.
  out.push({ kind: "tower", hex: at(1), integrity: WALL_INTACT, art: "" });
  out.push({ kind: "tower", hex: at(10), integrity: WALL_INTACT, art: "" });
  // No third turret on the board. It used to stand one cell behind the gate at
  // fortLevel 3, and a blocked cell on the causeway's own lane inside the
  // bailey is the one obstacle a player reads as a bug. The keep is scenery
  // now - `buildKeep` in `arena.ts` draws it in the margin, where the classic's
  // citadel stands - and the state's third tower entry is a hit-point record
  // only, which nothing in the kernel ties to a cell.
  void fortLevel;
  // Per-segment damage, because the whole point of drawing the works segment by
  // segment is that they fall segment by segment: one bay is bitten out of the
  // middle, one is down to its jambs and is the breach the attacker will use,
  // and the rest still stand. A screenshot of eleven identical intact bays says
  // nothing about the wrenchhand.
  // ONE damaged bay, and no destroyed one.
  //
  // The run used to carry a damaged bay and a breach, "because the whole
  // point of drawing the works segment by segment is that they fall segment
  // by segment". That argument holds for a wall with a body; it does not hold
  // for the ribbon (`build_siege.py` WIN_H2), where a destroyed bay is a
  // 45-arena-px hole with grass in it, and on the 2026-09-07 shot the two
  // gaps cut the curtain into three pieces that read as separate buildings.
  // The classic's own siege screenshots open on an intact wall
  // (a reference screenshot, a reference screenshot); the wrenchhand is
  // still drawn, on row 9, where a bitten crest shows against a run that is
  // otherwise continuous. Row 9 and not 3 or 8: those carry the scenery
  // turrets (`RUN_TURRET_ROWS`), and a spire over a breach is a tell.
  const damage: Readonly<Record<number, number>> = { 9: WALL_DAMAGED };
  for (const row of [0, 2, 3, 4, 6, 7, 8, 9]) {
    out.push({ kind: "wall", hex: at(row), integrity: damage[row] ?? WALL_INTACT, art: "" });
  }
  out.push({ kind: "gate", hex: at(5), integrity: WALL_INTACT, art: "" });
  // The ditch is cut in front of the whole run, end to end, on the attacker's
  // side of the wall line. The one cell it skips is the gate's, because that is
  // where the drawbridge lands.
  for (let row = 0; row < BF_HEIGHT; row++) {
    if (row === 5) continue;
    out.push({ kind: "moat", hex: at(row) - 1, integrity: 0, art: "" });
  }
  // No field obstacles on a siege board. A previous pass merged
  // `fixtureFieldObstacles` into the attacker's half on the argument that the
  // open ground read as one flat value; a blind critic then counted six
  // full-size props (two boulders, two trees, a stump and a bush) in front of
  // the walls, and none of the reference siege screenshots
  // carries a single one: a siege is fought over clear ground between the
  // besieger and the ditch. The ground litter (`scatterGroundProps`) is what
  // keeps the plain from measuring flat, and it blocks nothing.
  return out;
}

/**
 * Obstacles for an open-field screenshot scene.
 *
 * The board measured 13.1% object-scale relief inside the two deployment
 * columns - every prop was banked in the scenery margins and the playing area
 * itself was a bare plane. A classic battlefield is not a bare plane: it carries
 * rocks, trees and rubble *on the board*, which is what makes the ground read as
 * terrain and what gives the tactics their shape, because a shooter behind a
 * boulder and a walker who has to go round it are the whole reason the arena is
 * not a corridor.
 *
 * Like `fixtureSiegeObstacles` this is fixture data for the renderer - the
 * kernel does not author battlefield obstacles yet - but it goes in through
 * `startBattle`, so what is drawn here is what the rules block on. Nothing is
 * painted that a stack could walk through.
 *
 * Two invariants keep the scene playable:
 *   - columns 0-2 and 12-14 are left clear, so neither army starts boxed in and
 *     both have a free column to form up in;
 *   - no two obstacles are on adjacent cells. Adjacency in this staggered grid
 *     needs |dcol| <= 1 and |drow| <= 1, so requiring one of the two to be >= 2
 *     is enough, and it means a run of them can never fuse into a wall.
 */
export function fixtureFieldObstacles(seed: number): readonly Obstacle[] {
  const out: Obstacle[] = [];
  const placed: { col: number; row: number }[] = [];
  const free = (col: number, row: number): boolean =>
    placed.every((p) => Math.abs(p.col - col) >= 2 || Math.abs(p.row - row) >= 2);
  for (let row = 0; row < BF_HEIGHT; row++) {
    for (let col = 3; col <= BF_WIDTH - 4; col++) {
      const hex = hexIndex(col, row);
      // Roughly one cell in five of the interior, which lands ~14 obstacles on
      // the 99 interior cells. The classic's own generator sits in the same band; at
      // one in three the board stops being a field and becomes a maze.
      if (noise(seed, hex * 13 + 5) > 0.21) continue;
      if (!free(col, row)) continue;
      placed.push({ col, row });
      out.push({
        kind: noise(seed, hex * 13 + 9) > 0.62 ? "tree" : "rock",
        hex,
        integrity: WALL_INTACT,
        art: "",
      });
    }
  }
  return out;
}
